/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { SNACKBAR_ALERT_DEFAULT_PROPS } from '../../shared/constants';
import { setSnackBarDisplay } from '../snackBar/snackBarActionCreator';
import { getValidationErrorMessages } from '../../utility/commonUtility';
import { showErrorAction, clearErrorAction } from "../error/errorActionCreator";
import { hideLoaderAction, showLoaderAction } from "../loader/loaderActionCreator";
import { getAllRealmAction, getRealmEntitlementsAction, getSelectedRealmDetailsAction, setSelectedRealmAction, getAllIdpAction, getByIdpStatusAction } from './realmActionCreator';
import { ICreateRealmRequest, IDeleteRealmRequest, IEditRealmRequest, IGetAllRealmRequest, IGetRealmDetailsRequest } from '../../models/Realms/RealmContracts';
import { createNewRealm, deleteRealm, getAllRealms, getRealmEntitlementsRequest, getRealmProvisioningStepsRequest, getSelectedRealmDetail, updateEntitlementsForSubEnterprise, updateRealm, getIdp, getBYDeliversIdpStatus } from '../../services/realm';

export const getRealms = async (dispatch: Function, realmPayload: IGetAllRealmRequest) => {
  let realms: any = [];
  let selectedRealm: any = {};
  let pagination = {
    pageNumber: 1,
    pageSize: 10,
    totalCount: 0,
  };
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const getRealmsResponse = await getAllRealms(realmPayload);
    if (getRealmsResponse.data.length) {
      realms = getRealmsResponse.data;
      pagination = getRealmsResponse.metadata;
      selectedRealm = realms[0];
      dispatch(getAllRealmAction(realms, selectedRealm, pagination, realms.length > 0 ? false : true));
    } else {
      dispatch(getAllRealmAction([], {}, pagination, true));
    }
    dispatch(hideLoaderAction());
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in fetching realm: ' + errorMessageDetail
    };
    realms = [];
    selectedRealm = {};
    dispatch(getAllRealmAction(realms, selectedRealm, pagination, false));
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in fetching realm: ' + errorMessageDetail,
        severity: 'error',
      })
    );
  }
};

export const createRealm = async (dispatch: Function, createRealmPayload: ICreateRealmRequest) => {
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const createRealmResponse = await createNewRealm(createRealmPayload);
    if (createRealmResponse.id) {
      getRealms(dispatch, { customerId: createRealmPayload.customerId, subEnterpriseId: createRealmPayload.subEnterpriseId });
    }
    dispatch(hideLoaderAction());
    dispatch(setSelectedRealmAction(createRealmResponse));
    return createRealmResponse.realmName;
  } catch (error: any) {
    let errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in create realm: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in create realm: ' + errorMessageDetail,
        severity: 'error',
      })
    );
    return false;
  }
};

export const editRealm = async (dispatch: Function, editRealmPayload: IEditRealmRequest) => {
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    await updateRealm(editRealmPayload);
    getRealms(dispatch, { customerId: editRealmPayload.customerId, subEnterpriseId: editRealmPayload.subEnterpriseId });
    dispatch(hideLoaderAction());
    return true;
  } catch (error: any) {
    let errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in edit realm: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in edit realm: ' + errorMessageDetail,
        severity: 'error',
      })
    );
    return false;
  }
};

export const removeRealm = async (dispatch: Function, deleteRealmPayload: IDeleteRealmRequest) => {
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    await deleteRealm(deleteRealmPayload);
    getRealms(dispatch, { customerId: deleteRealmPayload.customerId, subEnterpriseId: deleteRealmPayload.subEnterpriseId });
    dispatch(hideLoaderAction());
    return true;
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in delete realm: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in delete realm: ' + errorMessageDetail,
        severity: 'error',
      })
    );
  }
};

export const getRealmDetails = async (dispatch: Function, realmDetailPayload: IGetRealmDetailsRequest) => {
  let selectedRealm: any = {};
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const getRealmDetailResponse = await getSelectedRealmDetail(realmDetailPayload);
    if (getRealmDetailResponse.realmName) {
      selectedRealm = getRealmDetailResponse;
      dispatch(getSelectedRealmDetailsAction(selectedRealm));
    }
    dispatch(hideLoaderAction());
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in fetching realm details: ' + errorMessageDetail
    };
    selectedRealm = {};
    dispatch(getSelectedRealmDetailsAction({}));
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in fetching realm details: ' + errorMessageDetail,
        severity: 'error',
      })
    );
  }
};

export const getRealmEntitlements = async (dispatch: Function, realmEntitlementPayload: IGetRealmDetailsRequest) => {
  let realmEntitlements: any = [];
  try {
    dispatch(showLoaderAction("EditRealmEntitlementDialog"));
    dispatch(clearErrorAction());
    const realmEntitlementsResponse = await getRealmEntitlementsRequest(realmEntitlementPayload);
    if (realmEntitlementsResponse) {
      realmEntitlements = realmEntitlementsResponse;
      dispatch(getRealmEntitlementsAction(realmEntitlements));
    }
    dispatch(hideLoaderAction('EditRealmEntitlementDialog'));
    return realmEntitlements;
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in fetching realm entitlements: ' + errorMessageDetail
    };
    realmEntitlements = {};
    dispatch(getSelectedRealmDetailsAction({}));
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction('EditRealmEntitlementDialog'));
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in fetching realm entitlements: ' + errorMessageDetail,
        severity: 'error',
      })
    );
    return realmEntitlements;
  }
};

export const updateRealmEntitlements = async (dispatch: Function, realmEntitlementPayload: IGetRealmDetailsRequest) => {
  try {
    dispatch(showLoaderAction("EditRealmEntitlementDialog"));
    dispatch(clearErrorAction());
    await updateEntitlementsForSubEnterprise(realmEntitlementPayload);
    // getRealmEntitlements(dispatch, {
    //   id: realmEntitlementPayload.id,
    //   customerId: realmEntitlementPayload.customerId,
    //   subEnterpriseId: realmEntitlementPayload.subEnterpriseId
    // });
    dispatch(hideLoaderAction('EditRealmEntitlementDialog'));
    return true;
  } catch (error: any) {
    let errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in Update Account Entitlement: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction('EditRealmEntitlementDialog'));
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in Update Account Entitlement: ' + errorMessageDetail,
        severity: 'error',
      })
    );
    return false;
  }
};

export const getRealmProvisioningSteps = async (dispatch: Function, payload: any) => {
  let provisioningSteps: any = [];
  const { customerId, subEnterpriseId: accountId, id: realmId } = payload;
  try {
    // dispatch(showLoaderAction("EditRealmProvisioningDialog"));
    dispatch(clearErrorAction());
    const provisioningStepsResponse = await getRealmProvisioningStepsRequest(customerId, accountId, realmId);
    if (provisioningStepsResponse) {
      provisioningSteps = provisioningStepsResponse;
      // dispatch(getRealmProvisioningStepsAction(provisioningSteps));
    }
    dispatch(hideLoaderAction('EditRealmProvisioningDialog'));
    return provisioningSteps;
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in fetching realm provisioning steps: ' + errorMessageDetail
    };
    provisioningSteps = {};
    // dispatch(getRealmProvisioningStepsAction({}));
    dispatch(showErrorAction(errorMessage));
    // dispatch(hideLoaderAction('EditRealmProvisioningDialog'));
    return provisioningSteps;
  }
};

export const getAllIdp = async (dispatch: Function) => {
  let idps: any = [];
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const getIdpsResponse = await getIdp();  
    if (getIdpsResponse.data.length) {
      dispatch(getAllIdpAction(getIdpsResponse));
    }     
  } catch (error: any) {    
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in fetching Idps: ' + errorMessageDetail
    };    
    dispatch(showErrorAction(errorMessage));    
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in fetching Idps: ' + errorMessageDetail,
        severity: 'error',
      })
    );
  } finally {
    dispatch(hideLoaderAction());
    return idps;
  }
};
export const getBYDeliversIdp = async (dispatch: Function) => {
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const byDeliversIdpStatus = await getBYDeliversIdpStatus();
    dispatch(getByIdpStatusAction(byDeliversIdpStatus));

  } catch (error: any) {    
    const errorMessageDetail = getValidationErrorMessages(error); 
    const errorMessage = {
      message: 'Error in fetching BlueYonder Support Idp status: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in fetching BlueYonder Support Idp status: ' + errorMessageDetail,
        severity: 'error',
      })
    );
  } finally {
    dispatch(hideLoaderAction());
  }
};